import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { errorMessage } from 'config/constants';
import { Project, Scenario } from 'types/pricingSimulator';
import _ from 'lodash';
import { pricingSimulatorUrls } from 'config/urls';
import { pollingStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import dayjs from 'dayjs';

interface FetchScenarioStatusProp {
    updateScenarioProperty: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void;
    variables?: any;
    queryKey: any;
    queryConfig?: any;
    selectedProjectInfo: Project | null;
    setSelectedProjectInfo: Function;
    refetchScenarioTableData: Function;
    refetchLensConfigData: Function;
}

function useFetchScenarioStatus({
    variables,
    queryKey,
    queryConfig = {},
    updateScenarioProperty,
    setSelectedProjectInfo,
    refetchScenarioTableData,
    refetchLensConfigData,
}: FetchScenarioStatusProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [shouldRefetchTableData, setShouldRefetchTableData] = useState(false);
    // Queries
    const {
        data,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            try {
                const data = await axios({
                    method: 'get',
                    url: pricingSimulatorUrls.getScenarioStatusById,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    useEffect(() => {
        //success handling
        if (apiSuccess && data) {
            onFetchComplete(data);
        }
    }, [data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload?.data?.status) {
                //update status and last updated at
                const updatedAtText = dayjs.unix(payload?.data?.updated_at).fromNow();
                if (!pollingStatuses.includes(payload?.data?.status)) {
                    setSelectedProjectInfo((prevScenarioInfo: Project | null) => {
                        let tempScenarioInfo: any = prevScenarioInfo || {};
                        const newScenarios = tempScenarioInfo?.simulationList?.map((item: Scenario) => {
                            return {
                                ...item,
                                status:
                                    Number(variables?.scenario_id) === item?.scenarioId
                                        ? payload?.data?.status
                                        : item?.status,
                                updatedAt:
                                    Number(variables?.scenario_id) === item?.scenarioId
                                        ? updatedAtText
                                        : item?.updatedAt,
                            };
                        });
                        const parsedData = {
                            ...tempScenarioInfo,
                            simulationList: newScenarios || [],
                        };
                        return parsedData;
                    });
                    // setSelectedProjectInfo({
                    //     ...selectedProjectInfo,
                    //     simulationList: newScenarios,
                    // });
                    //refetch data if status is coming from inprogress/setup
                    if (shouldRefetchTableData) {
                        refetchScenarioTableData();
                        refetchLensConfigData();
                        setShouldRefetchTableData(false);
                    }
                } else {
                    //only set to true if status is coming from inprogress/setup
                    setShouldRefetchTableData(true);
                }
                updateScenarioProperty('status', payload?.data?.status);
                updateScenarioProperty('updatedAt', updatedAtText);
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setLoading(false);
    }
    const outputData: any = {
        loading,
        refetch,
    };
    return outputData;
}

export default useFetchScenarioStatus;
